import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Phone, Star } from 'lucide-react';
import { companyInfo } from '../data/mock';

// Featured showroom image with both cars
const FEATURED_SHOWROOM_IMAGE = 'https://customer-assets.emergentagent.com/job_1ba9c20f-863a-4424-adf2-e244681c81bc/artifacts/h5mtpplh_ChatGPT%20Image%20Mar%205%2C%202026%2C%2001_43_02%20AM.png';

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Subtle parallax effect on mouse move
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 50;
    const y = (e.clientY - rect.top - rect.height / 2) / 50;
    setMousePosition({ x, y });
  };

  const scrollToInquiry = () => {
    document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToFleet = () => {
    document.getElementById('fleet')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen bg-black overflow-hidden" onMouseMove={handleMouseMove}>
      {/* Animated background elements */}
      <div className="absolute inset-0">
        {/* Gold gradient orbs - enhanced */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-amber-500/8 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-amber-600/8 rounded-full blur-[100px] animate-float animation-delay-500" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-400/5 rounded-full blur-[150px] animate-pulse-slow" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(rgba(212,160,23,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(212,160,23,0.4) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
        
        {/* Animated light streaks */}
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-amber-500/20 to-transparent animate-streak" />
        <div className="absolute top-0 right-1/3 w-px h-full bg-gradient-to-b from-transparent via-amber-400/15 to-transparent animate-streak animation-delay-1000" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-16">
        {/* Header text */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full mb-6 animate-fadeInDown backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-amber-400 animate-pulse" />
            <span className="text-amber-400 text-sm font-medium tracking-wide">Premium Luxury Rentals</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 animate-fadeInUp">
            <span className="block">Experience</span>
            <span className="block text-gold-animated mt-2">Luxury Driving</span>
          </h1>
          
          <p className="text-base sm:text-lg text-gray-400 max-w-xl mx-auto animate-fadeInUp animation-delay-200">
            Your dream car awaits. Premium vehicles for unforgettable moments in the Greater Toronto Area.
          </p>
        </div>

        {/* Featured Showroom Image - THE HERO */}
        <div className="relative max-w-5xl mx-auto mb-10">
          {/* Outer glow frame */}
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/30 via-amber-400/50 to-amber-500/30 rounded-3xl blur-xl opacity-60 animate-pulse-slow" />
          
          {/* Main image container */}
          <div 
            className="relative rounded-2xl sm:rounded-3xl overflow-hidden border border-amber-500/40 shadow-[0_0_80px_rgba(212,160,23,0.3)]"
            style={{
              transform: `perspective(1000px) rotateX(${mousePosition.y * 0.5}deg) rotateY(${mousePosition.x * 0.5}deg)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            {/* Shimmer overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-shimmer-slow z-10 pointer-events-none" />
            
            {/* Loading skeleton */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 animate-pulse flex items-center justify-center">
                <div className="text-amber-500/50 text-xl">Loading...</div>
              </div>
            )}
            
            {/* The showroom image */}
            <img
              src={FEATURED_SHOWROOM_IMAGE}
              alt="Range Rover and BMW X4 in Grand Line Rentals Showroom"
              className={`w-full h-auto object-cover transition-all duration-700 ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'}`}
              onLoad={() => setImageLoaded(true)}
              style={{
                filter: 'contrast(1.05) saturate(1.1)'
              }}
            />
            
            {/* Bottom gradient overlay for text readability */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
            
            {/* Featured badge */}
            <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-20">
              <div className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-black/70 backdrop-blur-md border border-amber-500/50 rounded-full animate-fadeInDown">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 fill-amber-400" />
                <span className="text-amber-400 text-xs sm:text-sm font-semibold tracking-wide">FEATURED VEHICLES</span>
              </div>
            </div>
            
            {/* Car labels */}
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 flex justify-between items-end z-20">
              <div className="text-left">
                <p className="text-amber-400/80 text-xs sm:text-sm font-medium mb-1">Range Rover Velar</p>
                <p className="text-white text-lg sm:text-2xl font-bold">
                  <span className="text-amber-400">$250</span>
                  <span className="text-gray-400 text-sm sm:text-base font-normal">/day</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-amber-400/80 text-xs sm:text-sm font-medium mb-1">BMW X4 M40i</p>
                <p className="text-white text-lg sm:text-2xl font-bold">
                  <span className="text-amber-400">$250</span>
                  <span className="text-gray-400 text-sm sm:text-base font-normal">/day</span>
                </p>
              </div>
            </div>
          </div>
          
          {/* Reflection effect */}
          <div 
            className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-[80%] h-20 bg-gradient-to-b from-amber-500/10 to-transparent blur-2xl opacity-50"
          />
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 animate-fadeInUp animation-delay-400">
          <button
            onClick={scrollToInquiry}
            data-testid="hero-inquire-btn"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 btn-gold px-8 py-4 rounded-xl text-lg font-bold shadow-[0_0_30px_rgba(212,160,23,0.4)] hover:shadow-[0_0_50px_rgba(212,160,23,0.6)] transition-all duration-300"
          >
            <Phone className="w-5 h-5" />
            Inquire Now
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          
          <button
            onClick={scrollToFleet}
            data-testid="hero-browse-fleet-btn"
            className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-amber-500/50 hover:border-amber-400 text-amber-400 hover:text-amber-300 rounded-xl text-lg font-semibold transition-all duration-300 hover:bg-amber-500/10"
          >
            Browse Full Fleet
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10 mt-10 animate-fadeInUp animation-delay-600">
          <div className="flex items-center gap-2 text-gray-400">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
              ))}
            </div>
            <span className="text-sm">5.0 Rated</span>
          </div>
          <div className="text-gray-500 hidden sm:block">|</div>
          <div className="text-gray-400 text-sm">50+ Premium Vehicles</div>
          <div className="text-gray-500 hidden sm:block">|</div>
          <div className="text-gray-400 text-sm">GTA's #1 Luxury Rentals</div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default HeroSection;
